<template>
  <div
    ref="bar"
    class="subscription-status-bar"
  >
    <div
      v-if="showBar"
      class="d-block d-md-flex justify-content-between align-items-center"
    >
      <div>
        <div class="text-large font-weight-semi-bold text-white">
          {{ message.title }}
        </div>
        <div class="text-normal font-weight-semi-bold text-muted">
          {{ message.description }}
        </div>
      </div>
      <div
        v-check-type="USER_TYPES.OWNER"
        class="d-flex align-items-center mt-1 mt-md-0 justify-content-end buttons-list"
        :class="{'flex-wrap': buttons.length > 2}"
      >
        <b-dropdown
          v-if="isAddonsReactive && buttons.length > 1"
          class="reactive-dropdown"
          variant="light"
          boundary="viewport"
          dropleft
          no-caret
        >
          <template #button-content>
            <feather-icon
              v-if="!isRTL"
              icon="ChevronLeftIcon"
            />
            <feather-icon
              v-if="isRTL"
              icon="ChevronRightIcon"
            />
            {{ $t('subscription.reactive') }}
          </template>
          <b-dropdown-item
            v-for="(button, idx) in buttons"
            :key="idx"
            @click="checkSwitchBranch(button.link)"
          >
            {{ button.title }}
          </b-dropdown-item>
        </b-dropdown>
        <template v-else>
          <b-button
            v-for="(button, idx) in buttons"
            :key="idx"
            :size="button.size || 'lg'"
            :class="{'mr-1': buttons.length > 1 && idx !== buttons.length - 1}"
            :disabled="loading || (button.link && button.link.name === $route.name)"
            :variant="button.variant || 'light'"
            @click="handleClickButton(button)"
          >
            <b-spinner
              v-if="loading"
              small
            />
            <span v-else>
              {{ button.title }}
            </span>
          </b-button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BButton, BSpinner, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import debounce from 'lodash/debounce'
import LocalizationService from '@/services/localization-service'
import SubscriptionAPIs from '@/API/subscription/subscription-api'
import StorageService from '@/services/storage-service'
import store from '@/store'
import PermissionMixin from '@/mixins/permission-mixin'

export default {
  name: 'SubscriptionStatusBar',
  components: {
    BButton, BSpinner, BDropdown, BDropdownItem,
  },
  mixins: [PermissionMixin],
  data() {
    return {
      loading: false,
      hideGraceAlert: false,
    }
  },
  computed: {
    ...mapGetters({
      branches: 'branches/branches',
      isExpired: 'subscription/isExpired',
      expiresIn: 'subscription/expiresIn',
      expiresSoon: 'subscription/expiresSoon',
      isRecurringSubscriptionPayment: 'subscription/isRecurringSubscriptionPayment',
      inGrace: 'subscription/inGrace',
      graceType: 'subscription/graceType',
      canRenew: 'subscription/canRenew',
      canExtendTrial: 'subscription/canExtendTrial',
      isTrial: 'subscription/isTrial',
      addons: 'subscription/currentAddons',
      isAuthenticated: 'auth/isAuthenticated',
      windowWidth: 'app/windowWidth',
    }),
    showBar() {
      return this.isAuthenticated && !!this.message
    },
    // Messages
    message() {
      const {
        expiresSoon,
        inGrace,
        graceType,
      } = this

      const hideGrace = this.hideGraceAlert && graceType === 'recurring'

      if (inGrace && !hideGrace) {
        return this.graceMessage
      }
      if (expiresSoon) {
        return this.expiresSoonMessage
      }
      return this.expiredAddonsMessage
    },
    graceMessage() {
      return {
        title: this.$t('subscription.you-in-grace-period'),
        description: this.graceType === 'renew' ? this.$tc('subscription.you-in-renew-grace', this.expiresIn, [this.expiresIn]) : this.$tc('subscription.you-in-recurring-grace', this.expiresIn, [this.expiresIn]),
      }
    },
    expiresSoonMessage() {
      return {
        title: this.isTrial ? this.$tc('subscription.trial-ends-in', this.expiresIn, [this.expiresIn]) : this.$tc('subscription.subscription-expires-in', this.expiresIn, [this.expiresIn]),
        description: this.isTrial ? this.$t('subscription.trial-ends-in-description') : this.$t('subscription.subscription-expires-in-description'),
      }
    },
    getExpiredAddonsDetails() {
      const today = this.$moment().startOf('day')
      let addons = this.addons.filter(addon => addon.expired && today.isSame(this.$moment(addon.pivot_expiry), 'day'))
      let period = 0
      let expired = true

      let willExpireAddons = this.addons.filter(addon => {
        const diff = this.$moment(addon.pivot_expiry).diff(today, 'day')
        return addon.pivot_expiry && diff <= 3 && diff > 0
      })

      if (!addons.length) {
        period = willExpireAddons.reduce((prev, addon) => Math.min(prev, this.$moment(addon.pivot_expiry)
          .diff(today, 'day')), Infinity)
        expired = false
        addons = willExpireAddons
        willExpireAddons = []
      }

      if (!addons.length || period < 0) return null
      return [addons, expired, period, willExpireAddons]
    },
    expiredAddonsMessage() {
      const [addons, expired, period] = this.getExpiredAddonsDetails ?? []
      if (!addons || !addons.length) return null

      const addonsName = [...new Set(addons.map(addon => LocalizationService.getTranslate(addon)))]
        .join(', ')

      return {
        title: expired ? this.$t('subscription.addon-expired', [addonsName]) : this.$tc('subscription.addon-will-expires', period + 1, [addonsName, period]),
        description: expired ? this.$t('subscription.addon-expired-description') : this.$t('subscription.addon-will-expires-description'),
      }
    },
    isAddonsReactive() {
      return !this.inGrace && !this.expiresSoon && !!this.expiredAddonsMessage
    },
    // Case based buttons
    buttons() {
      const {
        expiresSoon,
        inGrace,
      } = this

      if (inGrace) {
        return this.graceButtons
      }
      if (expiresSoon) {
        return this.expiresSoonButtons
      }
      return this.expiredAddonsButtons
    },
    graceButtons() {
      return this.graceType === 'renew' ? [this.payButton] : []
    },
    expiresSoonButtons() {
      if (this.isTrial) {
        return this.canExtendTrial ? [this.extendButton, this.pickPlanButton] : [this.pickPlanButton]
      }

      if (!this.canRenew) return [this.pickPlanButton]

      return [this.renewButton]
    },
    expiredAddonsButtons() {
      return this.reactiveButtons
    },
    // Absolute buttons
    renewButton() {
      return {
        title: this.$t('subscription.renew'),
        link: { name: 'billing:invoice', params: { id: 'renew' } },
      }
    },
    reactiveButtons() {
      const details = this.getExpiredAddonsDetails ?? [[]]
      const addons = details[0].concat(details[3]).filter(Boolean)

      return addons.map(addon => {
        const branchName = this.branches.find(branch => branch.id === addon.branch_id)?.name
        const addonName = addons.length > 1 ? `${this.$t('subscription.reactive')} ${LocalizationService.getTranslate(addon, 'name') || addon.slug}` : `${this.$t('subscription.reactive')}`
        const title = branchName ? `${addonName} (${branchName})` : `${addonName}`
        const limit = this.$store.getters['purchase/isAddonHasCount'](addon.slug) ? addon.limit : null
        return {
          title,
          link: { name: 'billing:plans', query: { reactive: addon.slug, branch_id: addon.branch_id, limit } },
        }
      })
    },
    payButton() {
      return {
        title: this.$t('payment.pay'),
        link: this.renewButton.link,
      }
    },
    pickPlanButton() {
      return {
        title: this.$t('subscription.pick-plan'),
        link: { name: 'billing:plans' },
      }
    },
    extendButton() {
      return {
        variant: 'outline-light',
        title: this.$t('subscription.extend-trial-action'),
        method: 'extendTrial',
      }
    },
  },
  watch: {
    showBar() {
      this.$nextTick(() => this.$emit('update:height', this.$refs.bar.clientHeight))
    },
    windowWidth() {
      if (this.showBar) {
        this.debounceEmitContainerHeight()
      }
    },
  },
  mounted() {
    this.hideGraceAlert = StorageService.getItem('okmenu_hide_grace', false, store.getters['auth/user']?.id)
    this.$nextTick(() => this.$emit('update:height', this.$refs.bar.clientHeight))
  },
  methods: {
    debounceEmitContainerHeight: debounce(function () {
      this.$emit('update:height', this.$refs.bar.clientHeight)
    }, 250),
    checkSwitchBranch(route) {
      const branchId = route?.query?.branch_id
      if (branchId && branchId !== this.$store.getters['branches/currentBranchId']) {
        return this.$store.dispatch('branches/SET_BRANCH', branchId).finally(() => this.$router.push(route))
      }
      return this.$router.push(route)
    },
    handleClickButton(button) {
      if (button.link) {
        this.checkSwitchBranch(button.link)
      } else if (button.method) {
        this[button.method]()
      }
    },
    async extendTrial() {
      this.loading = true
      try {
        const response = await SubscriptionAPIs.extendTrial()
        await this.$store.dispatch('subscription/FETCH_SUBSCRIPTION_DETAILS', true)
        this.$notifySuccess(response.message)
      } catch (error) {
        this.$notifyError(error)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss">
.subscription-status-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 13;

  & > div {
    margin-bottom: 2px;
    background: #000000;
    color: #ffffff;
    padding: 17.5px 28.5px;

    .btn-link {
      color: var(--secondary) !important;
      font-weight: 600!important;
      font-size: 14px!important;
    }
  }

  .btn {
    padding: 11.5px 13.5px!important;
    font-size: 12px!important;
    line-height: 1.25!important;
  }

  .reactive-dropdown {
    .dropdown-menu {
      z-index: 14;
    }
  }
}
</style>
